'use client';

import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Method,
  type MethodGroup,
  MethodGroupType,
} from '@/api/cms/paymentMethods';
import { Button } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { TabNavigation } from '@/components/TabNavigation';
import { Modals } from '@/helpers/modals';
import { detectTranslation } from '@/helpers/transl';
import { useToggle } from '@/hooks/common';
import { useRestrictRoutesForEstonia } from '@/hooks/estonia';
import { useNavigation } from '@/hooks/navigation';
import { useCMSPaymentMethods } from '@/modules/common/hooks/useCMSPaymentMethods';
import { brandSizeMap, getBrandKey } from '@/modules/payments';
import { useGetCommonStore, useModalsStore } from '@/store';
import { ScContainer, ScStaticPage } from '@/styles/common';
import {
  ScPageTitle,
  ScPaymentMethodsCategory,
  ScPaymentsGrid,
  ScPaymentsTable,
  ScPaymentsTableRow,
  ScPaymentsTabsItem,
  ScPaymentsText,
} from './styled';

interface PaymentMethodItemProps {
  method: Method;
  buttonProps: PaymentMethodButtonProps;
}

const paymentTypes = [MethodGroupType.DEPOSIT, MethodGroupType.WITHDRAW];

export const Payments: FC = () => {
  const { t } = useTranslation('common');

  useRestrictRoutesForEstonia();
  const useCommonStore = useGetCommonStore();

  const { cmsPaymentMethods, isLoading: pendingPaymentMethods } =
    useCMSPaymentMethods();
  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const { navigate } = useNavigation();

  const [currentPayTypeIndex, setCurrentPayIndex] = useState(0);

  const paymentTypesTranslates = useMemo(
    () => paymentTypes.map((type) => t(`button.${type}`)),
    [t]
  );

  const paymentMethodGropus = useMemo(() => {
    if (!cmsPaymentMethods) return [];

    return cmsPaymentMethods.methodsGroups.map(({ methods, ...rest }) => ({
      methods: methods.filter(({ paymentMethodsPage }) => paymentMethodsPage),
      ...rest,
    }));
  }, [cmsPaymentMethods]);

  const currentPayType = useMemo(() => {
    const type = paymentMethodGropus.find(
      ({ type }) => type === paymentTypes[currentPayTypeIndex]
    );
    return type ?? paymentMethodGropus[0];
  }, [paymentMethodGropus, currentPayTypeIndex]);

  useEffect(() => {
    if (pendingPaymentMethods) return;

    if (!cmsPaymentMethods) return navigate('/');
  }, [cmsPaymentMethods, pendingPaymentMethods, navigate]);

  if (!(cmsPaymentMethods && currentPayType)) return null;

  const { titleH1, text } = cmsPaymentMethods;
  const { id, title, type, methods } = currentPayType;

  return (
    <ScContainer>
      <ScPaymentsText className="paymentsText">
        <ScPageTitle>{titleH1}</ScPageTitle>
        <ScStaticPage>
          <div dangerouslySetInnerHTML={{ __html: text }} />
        </ScStaticPage>
      </ScPaymentsText>
      {isMobileDevice && (
        <div className="categories-container">
          <TabNavigation
            maxWidth100
            menuItemWidth="50%"
            navItems={paymentTypesTranslates}
            selectedItem={currentPayTypeIndex}
            setSelectedItem={setCurrentPayIndex}
            withoutShadow
          />
        </div>
      )}
      {isMobileDevice ? (
        <PaymentMethodsCategory
          id={id}
          methods={methods}
          title={title}
          type={type}
        />
      ) : (
        paymentMethodGropus.map(({ id, title, type, methods }) => (
          <PaymentMethodsCategory
            id={id}
            key={id}
            methods={methods}
            title={title}
            type={type}
          />
        ))
      )}
    </ScContainer>
  );
};

const PaymentMethodsCategory = ({ title, methods, type }: MethodGroup) => {
  const { t } = useTranslation('common');
  const open = useModalsStore.use.open();
  const useCommonStore = useGetCommonStore();

  const { navigate } = useNavigation();

  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const handleClick = useCallback(() => {
    if (isAuthenticated) {
      open(Modals.Deposit);
    } else {
      open(Modals.Registration);
    }
  }, [isAuthenticated, open]);

  const buttonProps = useMemo(() => {
    if (type === MethodGroupType.DEPOSIT) {
      return {
        text: t('button.deposit'),
        onClick: handleClick,
      };
    }
    if (isAuthenticated) {
      return {
        text: t('button.withdraw'),
        onClick: () => navigate('/profile-custom/cashbox', true),
      };
    }
    return {
      text: t('button.withdraw'),
      onClick: () => open(Modals.Registration),
    };
  }, [type, t, handleClick, isAuthenticated, open, navigate]);

  return (
    <ScPaymentMethodsCategory>
      <h2 className="h2 payment text-uppercase">{title}</h2>
      <PaymentMethodsFactory
        buttonProps={buttonProps}
        paymentMethods={methods}
      />
    </ScPaymentMethodsCategory>
  );
};

interface PaymentMethodsFactoryProps {
  paymentMethods: Method[];
  buttonProps: PaymentMethodButtonProps;
}

const PaymentMethodsFactory: FC<PaymentMethodsFactoryProps> = (props) => {
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  return isMobileDevice ? (
    <PaymentMethodItems {...props} />
  ) : (
    <PaymentMethodTable {...props} />
  );
};

const PaymentMethodTable: FC<PaymentMethodsFactoryProps> = ({
  paymentMethods,
  buttonProps,
}) => {
  const { t } = useTranslation('common');

  return (
    <ScPaymentsTable>
      <ScPaymentsTableRow headerMode>
        <div>{t('paymentMethods.headers.paymentMethod')}</div>
        <div>{t('paymentMethods.headers.type')}</div>
        <div>{t('paymentMethods.headers.fee')}</div>
        <div>{t('paymentMethods.headers.processingTime')}</div>
        <div>{t('paymentMethods.headers.limitsPerTransaction')}</div>
        <div />
      </ScPaymentsTableRow>
      {paymentMethods.map(
        (
          { image, type, fee, processingTime, limitsPerTransaction, name },
          index
        ) => {
          const { width: widthSizesImages, height: heightSizesImages } =
            brandSizeMap[getBrandKey(name)];
          return (
            <ScPaymentsTableRow key={index}>
              <div className="method-img_container">
                <ImageComponent
                  alt={image?.name || name}
                  height={heightSizesImages}
                  src={image?.url || ''}
                  width={widthSizesImages}
                />
              </div>

              <div>{detectTranslation(type, t)}</div>
              <div>{detectTranslation(fee, t)}</div>
              <div>{detectTranslation(processingTime, t)}</div>
              <div dangerouslySetInnerHTML={{ __html: limitsPerTransaction }} />
              <div className="btn">
                <PaymentMethodButton {...buttonProps} />
              </div>
            </ScPaymentsTableRow>
          );
        }
      )}
    </ScPaymentsTable>
  );
};

const PaymentMethodItems: FC<PaymentMethodsFactoryProps> = ({
  paymentMethods,
  buttonProps,
}) => (
  <>
    <ScPaymentsGrid>
      {paymentMethods.map((method, index) => (
        <PaymentMethodItem
          buttonProps={buttonProps}
          key={index}
          method={method}
        />
      ))}
    </ScPaymentsGrid>
  </>
);

const PaymentMethodItem: FC<PaymentMethodItemProps> = ({
  method: { image, type, fee, processingTime, limitsPerTransaction, name },
  buttonProps,
}) => {
  const { t } = useTranslation('common');
  const [isContentVisible, toggleIsContentVisible] = useToggle();

  const { width: widthSizesImages, height: heightSizesImages } = useMemo(() => {
    const key = getBrandKey(name);
    return brandSizeMap[key];
  }, [name]);

  return (
    <ScPaymentsTabsItem isContentVisible={isContentVisible}>
      <div className="header" onClick={toggleIsContentVisible}>
        <div className="el">
          <div className="title">
            <ImageComponent
              alt="Title"
              className="title__img"
              height={heightSizesImages}
              src={image?.url || ''}
              width={widthSizesImages}
            />
          </div>
          <div className="value">{detectTranslation(type, t)}</div>
        </div>
        <div className="arrow">
          <IconComponent name="whiteArrowRight" size="18px" />
        </div>
      </div>

      <div className="content">
        <div className="el">
          <div className="title">{t('paymentMethods.headers.fee')}</div>
          <div className="value">{detectTranslation(fee, t)}</div>
        </div>
        <div className="el">
          <div className="title">
            {t('paymentMethods.headers.processingTime')}
          </div>
          <div className="value">{detectTranslation(processingTime, t)}</div>
        </div>
        <div className="el">
          <div className="title">
            {t('paymentMethods.headers.limitsPerTransaction')}
          </div>
          <div className="value">
            <div dangerouslySetInnerHTML={{ __html: limitsPerTransaction }} />
          </div>
        </div>
        <PaymentMethodButton {...buttonProps} />
      </div>
    </ScPaymentsTabsItem>
  );
};

interface PaymentMethodButtonProps {
  text: string;
  href?: string;
  onClick?: () => void;
}

const PaymentMethodButton: FC<PaymentMethodButtonProps> = ({
  text,
  ...props
}) => {
  const { t } = useTranslation('common');
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const buttonText = useMemo(() => {
    if (!isAuthenticated) return t('button.register');

    return text;
  }, [t, isAuthenticated, text]);

  return (
    <Button {...props} paddingX={37} primary small={!isMobileDevice}>
      {buttonText}
    </Button>
  );
};
